import React, { useState, useMemo } from "react";
import StatsPanel from "src/components/StatsPanel";
import InsightOfTheDayCard from "src/components/InsightOfTheDayCard";
import MonthReturnsChart from "src/components/MonthReturnsChart";
import TradesPreviewPanel from "src/components/TradesPreviewPanel";
import { calculateReturnsByDate } from "src/util/trades";
import DashboardInsightCard from "src/components/DashboardInsightCard";
import { useSuspenseQuery } from "@tanstack/react-query";
import { getAppDataGetAppDataQueryOptions } from "src/model/app-data";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { normalizeTrades } from "@/util/unifiedTradesApi";
import { createFileRoute } from "@tanstack/react-router";
import { useAccount } from "src/context/AccountContext";
import { AccountData, TradeResponse } from "src/model/client.schemas";
import DashboardZeroState from "src/components/DashboardZeroState";
import LoadingOverlay from "src/components/LoadingOverlay";
import { useUserTimezone } from "@/context/AppConfigContext";
import {
  getGetAppDataWithParamsQueryOptions,
  useNeedsAttributes,
} from "src/util/query_client";
import {
  getAccountActivityItems,
  isTrade,
  TradeOrActivity,
} from "@/util/activity_util";
import { InfographicExportDialog } from "@/components/infographic";
import { useListAccounts } from "@/model_v2/default";

export const Route = createFileRoute("/_app/dashboard")({
  component: DashboardPageWrapper,
  staticData: {
    title: "Dashboard",
  },
  pendingComponent: () => <LoadingOverlay isLoading={true} />,
});

function DashboardPageWrapper() {
  return <DashboardPage />;
}

function DashboardPage() {
  const userTimezone = useUserTimezone();
  const { selectedAccount } = useAccount();
  const useV2Api = useFeatureFlag("use_v2_imports");
  const [showExportDialog, setShowExportDialog] = useState(false);

  // Reactively check if any tag columns are visible in the trades table
  // This hook re-renders when column visibility changes and prefetches data
  const { needsAttributes, isLoadingAttributes } = useNeedsAttributes();

  // Fetch accounts to get initial_cash for equity curve
  const { data: accountsData } = useListAccounts();

  // Choose query options based on feature flag
  // Include attributes only if tag columns are configured
  const query = useV2Api
    ? getGetAppDataWithParamsQueryOptions({ include_attributes: needsAttributes })
    : getAppDataGetAppDataQueryOptions();

  // @ts-ignore - Tanstack Query v5 types seem off here
  const { data: appDataResponse } = useSuspenseQuery(query);

  const appData = appDataResponse?.data;

  // Get the initial cash for the selected account
  const initialCash = useMemo(() => {
    if (!selectedAccount || !accountsData?.data) return null;
    const account = accountsData.data.find((acc) => acc.id === selectedAccount);
    return account?.initial_cash ? parseFloat(account.initial_cash) : null;
  }, [selectedAccount, accountsData]);

  // Wait for the selected account to be set since we have accounts in the response.
  if (!selectedAccount && (appData?.account_data?.length ?? 0) > 0) {
    return null;
  }

  // Find the selected account data
  const accountData = appData?.account_data?.find(
    (account) => account.account === selectedAccount,
  );

  // Normalize trades from either API version
  const normalizedTrades = normalizeTrades(appData?.all_trades);

  // Get combined items (trades and cash activities) for the selected account
  const tableItems: TradeOrActivity[] = getAccountActivityItems(
    normalizedTrades,
    undefined,
    selectedAccount,
  );
  // Filter out only the trades needed by some dashboard components
  const accountTrades: TradeResponse[] = tableItems.filter(isTrade);

  if (!accountData || !tableItems) {
    // Show zero state if no account data OR no items (trades/activities) for the account
    return <DashboardZeroState />;
  }

  const returns_by_date = calculateReturnsByDate(accountTrades, userTimezone);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <StatsPanel items={tableItems} onShareClick={() => setShowExportDialog(true)} initialCash={initialCash} />
      </div>
      <div className="flex flex-wrap gap-5">
        <div className="flex min-w-[300px] flex-[2]">
          <MonthReturnsChart returns_by_date={returns_by_date} />
        </div>
        <div className="flex min-w-[300px] flex-1">
          <InsightOfTheDayCard tip_of_the_day={appData?.tip_of_the_day} />
        </div>
        <div className="flex min-w-[300px] flex-1">
          <DashboardInsightCard insight={accountData?.dashboard_insight} />
        </div>
      </div>
      <div>
        {/* Pass the combined tableItems to TradesPreviewPanel */}
        <TradesPreviewPanel items={tableItems} isLoadingAttributes={isLoadingAttributes} />
      </div>

      {/* Infographic Export Dialog */}
      <InfographicExportDialog
        isOpen={showExportDialog}
        onClose={() => setShowExportDialog(false)}
        trades={accountTrades}
        items={tableItems}
      />
    </div>
  );
}
